/**
 * Chat-workstream frontend types — the `ChatStreamEvent` union + session shapes, the
 * camelCase TS mirror of `backend/aletheia/chat/{events,session_store}.py` (editor-chat.md
 * §2.3, §2.4).
 *
 * NAMING (conventions.md §2.3): the over-the-wire JSON is `snake_case` (the backend's Pydantic
 * models); these are the `camelCase` TS shapes. The chat SSE consumer (`useChatStream`) maps
 * each inbound frame snake→camel — it is the chat surface's boundary mapping, the same role
 * `src/api/mappers.ts` plays for the REST client.
 *
 * The `ChatStreamEvent` union here mirrors the backend's `kind`-discriminated union exactly —
 * `text-delta` / `tool-call` / `tool-result` / `usage` / `done` / `error`. Every turn's event
 * stream ends with exactly one terminal event (`done` | `error`) — the no-silent-failure
 * contract (conventions.md §3, §7).
 */

import type { NoteId, NotebookId } from '@/types';

// ---------------------------------------------------------------------------
// Token usage (mirrors common/cost.py TokenUsage — already on the wire elsewhere)
// ---------------------------------------------------------------------------

/** The token breakdown a model call reports — mirrors the backend `TokenUsage`. */
export interface ChatTokenUsage {
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
}

// ---------------------------------------------------------------------------
// The stream-facing error category (mirrors chat/errors.py ChatErrorCategory)
// ---------------------------------------------------------------------------

/**
 * The stream-facing chat error category the `error` event carries — mirrors the backend
 * `ChatErrorCategory`. The chat surface branches on this to render the right inline state.
 * Note `iteration_limit` is NOT a failure — it is a clean bounded terminal state with the
 * partial answer kept (editor-chat.md §2.3 / chat/errors.py).
 */
export type ChatErrorCategory =
  | 'model_unavailable'
  | 'iteration_limit'
  | 'spend_cap'
  | 'tool_system_error'
  | 'malformed_output';

// ---------------------------------------------------------------------------
// The ChatStreamEvent union (mirrors chat/events.py)
// ---------------------------------------------------------------------------

/**
 * A multipass-phase boundary marker — the agent loop just started a new phase of the
 * draft → evaluate → refine workflow. The chat surface uses the `label` for the
 * "thinking…" indicator so the user understands *why* the wait is happening across
 * three model calls. `phase: 'refining'` is when text deltas actually start streaming
 * to the bubble; during `drafting` only tool affordances flow (the draft text is
 * internal), and during `evaluating` nothing streams (it's a cheap critique call).
 */
export interface PhaseEvent {
  kind: 'phase';
  /**
   * Which phase is starting. `drafting` / `evaluating` / `refining` are the multipass
   * refinement phases on the single-stage chat path. `synthesizing` is emitted by the
   * two-stage pipeline (#102 PR 3) when the researcher finishes and the writer call
   * opens — the client uses this signal to collapse the inline `<ThinkingBox>` to its
   * single-line summary state and start rendering the writer's bubble below it.
   */
  phase: 'drafting' | 'evaluating' | 'refining' | 'synthesizing' | 'polishing';
  label: string;
}

/** An incremental chunk of the assistant's text — appended to the streaming bubble. */
export interface TextDeltaEvent {
  kind: 'text-delta';
  text: string;
}

/** The agent invoked an MCP tool — rendered as an inline "running" affordance. */
export interface ToolCallEvent {
  kind: 'tool-call';
  callId: string;
  toolName: string;
  argsSummary: string;
  status: 'running';
}

/** An MCP tool call finished — `error` is the recoverable path (the error is fed back to the model). */
export interface ToolResultEvent {
  kind: 'tool-result';
  callId: string;
  status: 'ok' | 'error';
  resultSummary: string;
  /**
   * For WORKFLOW tools (`synthesize_topic`, `find_relationships`, etc.), the list of
   * atomic primitives the workflow composed server-side — backend extracts this from
   * the result's `tools_called` field. `null` for atomic tools that don't compose
   * anything. The UI renders this as a subtitle on the tool affordance so the user
   * sees the workflow → primitives chain without losing the single-tool-call UX.
   */
  innerTools: string[] | null;
  /**
   * Up to 3 note titles from the tool's result — the "what came back" snippet the
   * transparency UI shows so the user SEES the data flowing, not just that a tool ran.
   * Titles are truncated server-side (≤64 chars). `null` for non-list results (a
   * `get_note` read, a write, an aggregate) that have no title list to surface. Wire:
   * `result_titles` (snake→camel via `keysToCamel`).
   */
  resultTitles: string[] | null;
}

/**
 * The agent loop is about to run another model round (editor-chat.md §2.3 transparency).
 * Emitted just BEFORE each round's model call, this is the honest "the app is consulting
 * the model" signal the transparency UI renders — the round progress against the hard cap,
 * the coarse size of the context being sent, and how much older tool-result text was
 * condensed to keep that context bounded. The user sees the back-and-forth with the model
 * instead of an opaque "Thinking…".
 */
export interface ModelCallEvent {
  kind: 'model-call';
  /** The 1-based round about to run. */
  iteration: number;
  /** The hard round cap — the loop stops after this many rounds (`iteration_limit`). */
  maxIterations: number;
  /** A coarse token count of the context being sent this round — the "size of what we're sending". */
  approxInputTokens: number;
  /** How many older tool-results were condensed just before this call to bound context (0 = none). */
  compressedResults: number;
}

/** The turn's token/cost telemetry — emitted once at turn end (REVIEW M5). */
export interface UsageEvent {
  kind: 'usage';
  usage: ChatTokenUsage;
  /** The turn's estimated USD cost — a Decimal string on the wire (conventions.md §2.4). */
  costUsd: string;
  model: string;
}

/** The turn completed normally — the terminal success event. */
export interface DoneEvent {
  kind: 'done';
  messageId: string;
  iterations: number;
}

/** The turn ended in a surfaced error / bounded state — the terminal failure event. */
export interface ErrorEvent {
  kind: 'error';
  category: ChatErrorCategory;
  message: string;
  correlationId: string;
  /** Set when a partial answer was kept (iteration_limit / a mid-stream degradation). */
  messageId: string | null;
}

/** The streamed event union — `kind`-discriminated, mirrors the backend `ChatStreamEvent`. */
export type ChatStreamEvent =
  | PhaseEvent
  | TextDeltaEvent
  | ToolCallEvent
  | ToolResultEvent
  | ModelCallEvent
  | UsageEvent
  | DoneEvent
  | ErrorEvent;

/** The terminal event kinds — exactly one ends every turn. */
export const TERMINAL_EVENT_KINDS = ['done', 'error'] as const;

// ---------------------------------------------------------------------------
// Session shapes (mirrors chat/session_store.py)
// ---------------------------------------------------------------------------

/** One content block within a message — `type`-discriminated; mirrors `ChatContentBlock`. */
export type ChatContentBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; callId: string; toolName: string; args: Record<string, unknown> }
  | { type: 'tool_result'; callId: string; isError: boolean; content: string };

/** One message in a session — mirrors the backend `ChatMessage`. */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: ChatContentBlock[];
  modelId: string | null;
  usage: ChatTokenUsage | null;
  createdAt: string;
}

/** A stateful chat session — mirrors the backend `ChatSession`. */
export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  modelRole: 'chat';
  messages: ChatMessage[];
  usage: ChatTokenUsage;
  /** The NoteId "save session as note" produced; null until run. */
  savedAsNoteId: string | null;
}

/**
 * One session-list row — mirrors the backend `ChatSessionSummary` (perf E3). The list
 * endpoint no longer ships message bodies (they made the page ~500 KB); the sidebar renders
 * the session columns plus the two server-side aggregates below. Open a session via
 * `getChatSession` for the full history. The wire also carries an always-empty `messages`
 * key for older cached bundles — new code must not read it, so it is not typed here.
 */
export interface ChatSessionSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  modelRole: 'chat';
  usage: ChatTokenUsage;
  /** The NoteId "save session as note" produced; null until run. */
  savedAsNoteId: string | null;
  /** First ~160 chars of the newest text-bearing message; null for a session with no text.
   *  Optional so a newer bundle tolerates an older backend that predates the field. */
  preview?: string | null;
  /** Real user turns (text-bearing user messages). Optional — same wire-skew tolerance. */
  turnCount?: number;
}

/**
 * Whether a session summary is safe to AUTO-adopt as the active session after a fresh chat's
 * first turn (perf D5, ghost sessions): only a session that actually holds content. A failed
 * turn can leave an empty shell at the top of the most-recently-updated list; auto-opening it
 * strands the user in a blank conversation. The backend persists the user message at turn
 * start, so the session THIS client's send just created always has `turnCount >= 1` and
 * passes. An older backend that predates the summary aggregates (both fields absent from the
 * wire) keeps the historical adopt-always behavior.
 */
export function isAdoptableSession(session: ChatSessionSummary): boolean {
  if (session.turnCount === undefined && session.preview === undefined) return true;
  return (
    (session.turnCount ?? 0) > 0 ||
    (session.preview !== undefined && session.preview !== null && session.preview !== '')
  );
}

/** The `GET /api/v1/chat/sessions` response — a page of session summaries. */
export interface SessionListResponse {
  items: ChatSessionSummary[];
}

/** The `POST /api/v1/chat/sessions/{id}/save_as_note` result — mirrors `SaveSessionAsNoteResult`. */
export interface SaveSessionAsNoteResult {
  ok: boolean;
  sessionId: string;
  noteId: string | null;
  alreadySaved: boolean;
  errorCode: string | null;
  message: string;
}

// ---------------------------------------------------------------------------
// The client-side turn view-model (assembled by useChatStream from the event stream)
// ---------------------------------------------------------------------------

/**
 * One tool call as the chat UI renders it — assembled by the SSE consumer from a `tool-call`
 * event and its later matching `tool-result` event (correlated by `callId`).
 */
export interface ChatToolCallView {
  callId: string;
  toolName: string;
  argsSummary: string;
  /** `'running'` until the matching `tool-result` arrives, then its status. */
  status: 'running' | 'ok' | 'error';
  resultSummary: string | null;
  /**
   * For workflow tools, the atomic primitives the workflow composed server-side —
   * rendered as a subtitle on the affordance ("synthesize_topic → search_hybrid,
   * search_chunks"). `null` for atomic tools that don't compose anything. Populated
   * when the matching `tool-result` event arrives with `innerTools`.
   */
  innerTools: string[] | null;
  /**
   * Up to 3 note titles the tool returned — the "what came back" snippet the affordance
   * shows when expanded, so the user sees the data flowing, not just that a tool ran.
   * `null` until the matching `tool-result` arrives (and for non-list results that carry
   * no titles). Same populate-on-tool-result pattern as `innerTools`.
   */
  resultTitles: string[] | null;
}

/**
 * The streaming-turn view-model the SSE consumer builds incrementally as events arrive — the
 * chat UI renders *this*, not the raw event stream.
 *
 * `phase` is the turn's lifecycle: `streaming` while events are arriving, `done` after the
 * terminal `done`, `error` after the terminal `error` (the partial `text` is still rendered —
 * an `iteration_limit` / degradation keeps what was streamed).
 */
export interface StreamingTurn {
  phase: 'streaming' | 'done' | 'error';
  /** The assistant text accumulated from `text-delta` events so far. */
  text: string;
  /** The tool calls this turn made, in order, each with its (eventual) result. */
  toolCalls: ChatToolCallView[];
  /** The turn's token/cost telemetry — set once the `usage` event arrives. */
  usage: { usage: ChatTokenUsage; costUsd: string; model: string } | null;
  /** The terminal error, when the turn ended in `error` — drives the graceful-failure UI. */
  error: { category: ChatErrorCategory; message: string; correlationId: string } | null;
  /** The persisted assistant message id, once known (from `done` or a partial-keep `error`). */
  messageId: string | null;
  /**
   * The phase the agent loop most recently announced via a `phase` event —
   * `'drafting'` / `'evaluating'` / `'refining'` (multipass refinement, single-stage),
   * `'synthesizing'` (two-stage pipeline, writer call opening — #102 PR 3), or `null`
   * for single-pass mode or pre-first-phase. Drives the "thinking…" indicator label
   * AND the `<ThinkingBox>` collapse animation.
   */
  multipassPhase: 'drafting' | 'evaluating' | 'refining' | 'synthesizing' | 'polishing' | null;
  /** The label the backend supplied for `multipassPhase` — already human-readable. */
  multipassPhaseLabel: string | null;
  /**
   * The round the agent loop is currently running (1-based), from the most recent
   * `model-call` event — drives the "Consulting the model · round N of M" live status and
   * the activity timeline. `null` before the first `model-call` event (older backend, or a
   * turn that answered without a bounded loop).
   */
  currentRound: number | null;
  /** The hard round cap (`maxIterations`) from the most recent `model-call` event; `null` before the first. */
  maxRounds: number | null;
  /** The coarse context size of the most recent `model-call` round, in tokens; `null` before the first. */
  approxInputTokens: number | null;
  /**
   * The RUNNING SUM of `compressedResults` across every `model-call` event this turn — how
   * many older tool-results were condensed to keep context bounded. Starts at 0; surfaced
   * in the live status ("· condensed N earlier results") when > 0.
   */
  compressedResults: number;
}

// ---------------------------------------------------------------------------
// Request shapes (the chat surface sends these)
// ---------------------------------------------------------------------------

/** The `POST /api/v1/chat` request body — run one turn. */
export interface ChatTurnRequest {
  /** The session to continue; omit to start a fresh session. */
  sessionId?: string;
  userMessage: string;
  /** facelift-v3 — optional soft-routing hints for this turn. `workflow`
   *  biases the agent toward a named workflow/tool; `scope` is a human-readable
   *  retrieval scope ("notebook: 05 Active"). Both omitted = default behavior. */
  workflow?: string | null;
  scope?: string | null;
  /** Tool-surface tier for this turn: 'fast' (curated workflow tools, lower latency,
   *  the default) or 'deep' (full tool surface). Omitted = 'fast' on the backend. */
  mode?: 'fast' | 'deep';
}

/** The `POST /api/v1/chat/sessions/{id}/save_as_note` request body. */
export interface SaveAsNoteRequest {
  notebookId?: NotebookId;
}

/** A re-export so the chat surface's note-link affordance can type the produced note id. */
export type ChatProducedNoteId = NoteId;
